import React, { useState, useCallback } from 'react';
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../../config";

function useGetPromotionTriggers(params, enabled) {
    const queryParams = {
        limit: 50,
        offset: 0,
        ...params,
    };
    
    if (queryParams.limit > 50) {
        queryParams.limit = 50;
    }
    
    return useQuery({
        queryFn: async () => {
            const res = await api.get(`/promotions-trigger/triggers`, { params: queryParams });
            return res.data;
        },
        queryKey: ['promotion-triggers', queryParams],
        enabled,
        retry: false,
        refetchOnWindowFocus: false,
        keepPreviousData: true
    })
}

function useGetPromotionTrigger(triggerId, enabled) {
    return useQuery({
        queryFn: async () => {
            const res = await api.get(`/promotions-trigger/triggers/${triggerId}`);
            return res.data;
        },
        queryKey: ['promotion-trigger', triggerId],
        enabled,
        retry: false,
        refetchOnWindowFocus: false
    })
}

function useGetPromotionTriggerSettings(params, enabled) {
    const queryParams = {
        limit: 50,
        offset: 0,
        ...params,
    };
    
    if (queryParams.limit > 50) {
        queryParams.limit = 50;
    }
    
    return useQuery({
        queryFn: async () => {
            const res = await api.get(`/promotions-trigger/settings`, { params: queryParams });
            return res.data;
        },
        queryKey: ['promotion-trigger-settings', queryParams],
        enabled,
        retry: false,
        refetchOnWindowFocus: false,
        keepPreviousData: true
    })
}

// Hook para paginação infinita de settings por trigger
function useGetPromotionTriggerSettingsInfinite(triggerId, enabled = true) {
    const queryClient = useQueryClient();
    const [allSettings, setAllSettings] = useState([]);
    const [currentPage, setCurrentPage] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [hasNextPage, setHasNextPage] = useState(true);
    const [totalItems, setTotalItems] = useState(0);
    
    const loadPage = useCallback(async (page = 0, reset = false) => {
        if (!enabled || !triggerId) return;
        
        setIsLoading(true);
        try {
            const response = await api.get('/promotions-trigger/settings', {
                params: {
                    trigger_id: triggerId,
                    limit: 50,
                    offset: page * 50
                }
            });
            
            const { results, paging } = response.data;
            setTotalItems(paging.total);
            
            if (reset || page === 0) {
                setAllSettings(results);
            } else {
                setAllSettings(prev => [...prev, ...results]);
            }
            
            setHasNextPage(results.length === 50 && (page + 1) * 50 < paging.total);
            setCurrentPage(page);
        } catch (error) {
            console.error('Erro ao carregar settings:', error);
        } finally {
            setIsLoading(false);
        }
    }, [enabled, triggerId]);
    
    const loadNextPage = useCallback(() => {
        if (hasNextPage && !isLoading) {
            loadPage(currentPage + 1);
        }
    }, [currentPage, hasNextPage, isLoading, loadPage]);
    
    const reset = useCallback(() => {
        setAllSettings([]);
        setCurrentPage(0);
        setHasNextPage(true);
        loadPage(0, true);
    }, [loadPage]);
    
    const clearCache = useCallback(() => {
        setAllSettings([]);
        setCurrentPage(0);
        setHasNextPage(true);
        setTotalItems(0);
        setIsLoading(false);
        // Limpar também as queries relacionadas do React Query
        if (triggerId) {
            queryClient.removeQueries({ 
                queryKey: ['promotion-trigger-settings', triggerId],
                exact: false 
            });
        }
    }, [queryClient, triggerId]);
    
    // Carregar primeira página quando o hook for habilitado
    const [lastTriggerId, setLastTriggerId] = React.useState(null);
    
    React.useEffect(() => {
        if (enabled && triggerId && triggerId !== lastTriggerId) {
            setLastTriggerId(triggerId);
            setAllSettings([]);
            setCurrentPage(0);
            setHasNextPage(true);
            setTotalItems(0);
            loadPage(0, true);
        }
    }, [enabled, triggerId]); // SEM loadPage para evitar loop
    
    return {
        data: allSettings,
        totalItems,
        isLoading,
        hasNextPage,
        loadNextPage,
        reset,
        clearCache
    };
}

export {
    useGetPromotionTriggers,
    useGetPromotionTrigger,
    useGetPromotionTriggerSettings,
    useGetPromotionTriggerSettingsInfinite
}